import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { calculatorService, clientService, orderService, api } from '../../services/api';
import { TpvDrawer } from '../TPV';
import { useAuth } from '../../contexts/AuthContext';
import { clientDiscountSuffix, mostrarErrorAltaCliente, avisarAltaCliente } from '../../utils/clientDisplay';
import { clientesModoTienda } from '../../utils/clientSearch';
import { useOffline } from '../../context/OfflineContext';
import Select from 'react-select';
import apiService from '../../services/api';
import { NumericKeypad } from '../Common';

// Professional ERP-style layout with dense information
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
  background: #f0f0f0;
  overflow-x: hidden;

  @media (max-width: 900px) {
    padding: 0.5rem;
  }
`;

const StoreLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  max-height: calc(100vh - 250px);
  position: relative;
  margin-bottom: 2rem;
  
  .left-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    padding-right: 0.5rem;
    max-height: calc(100vh - 250px);
  }
  
  .calculator-section {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  .price-section {
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  .tpv-section {
    position: sticky;
    top: 1rem;
    height: calc(100vh - 250px);
    max-height: calc(100vh - 250px);
    overflow-y: auto;
  }
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;

    .left-column {
      overflow-y: visible;
      padding-right: 0;
      max-height: none;
    }

    .tpv-section {
      position: static;
      height: auto;
      max-height: none;
    }
  }

  @media (max-width: 900px) {
    gap: 0.5rem;

    .calculator-section,
    .price-section {
      padding: 0.5rem;
    }
  }
`;

const Header = styled.div`
  background: #f5f5f5;
  padding: 0.75rem 1rem;
  margin-bottom: 0;
  border: 1px solid #ddd;
  border-bottom: none;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 0.95rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #333;
    margin: 0;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$storeMode ? '1fr 1fr' : '1fr 1fr'};
  gap: ${props => props.$storeMode ? '2rem' : '1rem'};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 900px) {
    gap: 0.5rem;
  }
`;

const InputSection = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 0;
  padding: 1rem;
  box-shadow: none;
`;

const PricePanel = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 0;
  padding: 1rem;
  position: ${props => props.$storeMode ? 'static' : 'sticky'};
  top: 1rem;
  height: fit-content;
  box-shadow: none;

  @media (max-width: 1200px) {
    position: static;
  }
`;

const CompactForm = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const InlineGroup = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 0.5rem;
  align-items: end;
  width: 100%;
  overflow: visible;

  @media (max-width: 1400px) {
    grid-template-columns: ${props =>
      props.columns?.includes('100px') ? 'repeat(2, 1fr)' : props.columns || '1fr 1fr'
    };
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.25rem;
  }
`;

const QuickInput = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 0.85rem;
    color: #333;
    margin-bottom: 0.25rem;
    font-weight: 500;
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  input, select {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 0;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #5cb85c;
      background: #fff;
    }
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }

  textarea {
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 0;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #5cb85c;
      background: #fff;
    }
  }

  .unit {
    font-size: 0.9rem;
    color: #666;
  }
`;

const MaterialLine = styled.div`
  display: grid;
  grid-template-columns: 25px 2fr 1fr 70px 35px;
  gap: 0.4rem;
  align-items: center;
  padding: 0.4rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f8f8;
  }

  .index {
    font-size: 0.875rem;
    color: #999;
    text-align: center;
  }

  select {
    padding: 0.35rem;
    border: 1px solid #ddd;
    font-size: 0.9rem;
    line-height: 1.2;
  }

  input {
    padding: 0.35rem;
    border: 1px solid #ddd;
    font-size: 0.9rem;
    text-align: center;
    line-height: 1.2;
  }

  button {
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 3px;
    width: 26px;
    height: 26px;
    font-size: 0.95rem;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: #c82333;
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
  padding: 0;
  border-bottom: none;
  text-transform: none;
  color: #333;
  font-weight: 600;

  span {
    font-weight: 400;
    color: #666;
    font-style: italic;
    margin-left: 0.25rem;
  }
`;

const AddMaterialButton = styled.button`
  padding: 0.5rem 1rem;
  background: #5cb85c;
  color: white;
  border: none;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 0.75rem;
  text-transform: uppercase;

  &:hover {
    background: #4cae4c;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PriceLine = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: ${props => props.$large ? '1.25rem' : '1rem'};
  font-weight: ${props => props.$large ? '700' : '400'};
  color: ${props => props.$total ? '#2e7d32' : '#333'};
  border-top: ${props => props.$total ? '2px solid #ddd' : 'none'};
  margin-top: ${props => props.$total ? '0.5rem' : '0'};
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;

  button {
    padding: 1rem 1.5rem;
    border: none;
    border-radius: 0;
    font-weight: 500;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }

  .calculate {
    background: #5cb85c;
    color: white;

    &:hover:not(:disabled) {
      background: #4cae4c;
    }
  }

  .save {
    background: #5cb85c;
    color: white;

    &:hover:not(:disabled) {
      background: #4cae4c;
    }
  }

  .reset {
    background: #f0ad4e;
    color: white;

    &:hover:not(:disabled) {
      background: #ec971f;
    }
  }

  .secondary {
    background: #5bc0de;
    color: white;

    &:hover:not(:disabled) {
      background: #46b8da;
    }
  }
`;

const KeyboardHint = styled.span`
  font-size: 0.875rem;
  color: #999;
  margin-left: 0.5rem;
`;

const MaterialSection = styled.div`
  border: none;
  border-radius: 0;
  padding: 0.5rem 0;
  background: transparent;
  margin-bottom: 0.75rem;
`;

const MaterialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: #555;
`;

const MaterialList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  min-height: 30px;
`;

const MaterialItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.5rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  color: #999;
  font-size: 0.9rem;
  font-style: italic;
  padding: 0.25rem 0;
`;

const WarningMessage = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.95rem;
  color: #856404;
  display: flex;
  align-items: start;
  gap: 0.5rem;

  .warning-icon {
    flex-shrink: 0;
    font-size: 1.1rem;
  }

  .warning-text {
    flex: 1;
    line-height: 1.4;
  }
`;

const WarningModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const WarningModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const WarningModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  .icon {
    font-size: 2.5rem;
    flex-shrink: 0;
  }

  h3 {
    margin: 0;
    color: #856404;
    font-size: 1.25rem;
  }
`;

const WarningModalBody = styled.div`
  color: #333;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;

  strong {
    color: #856404;
  }
`;

const WarningModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`;

const PriceBreakdown = styled.div`
  background: #fff;
  border-radius: 0;
  padding: 0;
  margin-bottom: 1rem;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
  
  ${props => props.discount && `
    color: #dc3545;
  `}
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  border-top: 2px solid #333;
  margin-top: 0.5rem;
  color: #333;
`;

const MarginInfo = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const ModeToggle = styled.div`
  display: flex;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0;
  padding: 0;
  margin-bottom: 1rem;
  gap: 0;
`;

const ModeButton = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0;
  background: ${props => {
    if (!props.$active) return '#f5f5f5';
    return props.$isProfessional ? '#5cb85c' : '#5bc0de';
  }};
  color: ${props => props.$active ? 'white' : '#666'};
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;

  &:hover {
    background: ${props => {
      if (!props.$active) return '#e9e9e9';
      return props.$isProfessional ? '#4cae4c' : '#46b8da';
    }};
  }

  &:focus {
    outline: none;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 95%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.25rem 0;
  color: #333;
  font-size: 1.3rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FullWidthGroup = styled.div`
  grid-column: span 2;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &.primary {
    background-color: #4CAF50;
    color: white;

    &:hover {
      background-color: #45a049;
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background-color: #f5f5f5;
    color: #333;

    &:hover {
      background-color: #e0e0e0;
    }
  }
`;

// Create portal container outside component to avoid recreation
let selectPortalElement = null;
const getSelectPortal = () => {
  if (!selectPortalElement) {
    selectPortalElement = document.createElement('div');
    selectPortalElement.id = 'select-portal-root';
    selectPortalElement.style.position = 'fixed';
    selectPortalElement.style.top = '0';
    selectPortalElement.style.left = '0';
    selectPortalElement.style.zIndex = '9999';
    selectPortalElement.style.pointerEvents = 'auto';
    document.body.appendChild(selectPortalElement);
  }
  return selectPortalElement;
};

const CalculatorPro = ({
  editMode = false,
  editData = null,
  editOrder = null,
  onSaveEdit = null,
  onCancelEdit = null
}) => {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();
  const {
    isOnline,
    products: offlineProducts,
    clients: offlineClients,
    saveOrder: saveOrderOffline,
    createClient: createClientOffline,
    isInitialized: offlineInitialized
  } = useOffline();
  const [mode, setMode] = useState('professional'); // 'professional' or 'store'
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [ticketItems, setTicketItems] = useState([]); // Store ticket items from TPV
  const [addToTicketSignal, setAddToTicketSignal] = useState(0);
  const [tpvDrawerOpen, setTpvDrawerOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editInitialized, setEditInitialized] = useState(false);
  const [manualPrice, setManualPrice] = useState(null); // Manual price override for edit mode
  const [isManualPriceEnabled, setIsManualPriceEnabled] = useState(false);
  const isEditingTPVOrder = Boolean(
    editMode && editOrder && (editOrder.tpvTransaction || editOrder.metadata?.isTPVSale)
  );

  // Ensure portal is initialized on mount
  React.useEffect(() => {
    getSelectPortal();
  }, []);

  // Force professional mode for non-admin users
  useEffect(() => {
    if (!isAdmin && mode === 'store') {
      setMode('professional');
    }
  }, [isAdmin, mode]);

  // Alta de cliente desde la calculadora: en "venta tienda" nace particular (margen 0);
  // en "calculadora profesional" nace profesional (margen 100). Luego se puede cambiar.
  const margenPorDefecto = () => (mode === 'store' ? 0 : 100);
  const clienteVacio = () => ({ name: '', email: '', phone: '', street: '', street2: '', city: '', zip: '', taxId: '', marginPercentage: margenPorDefecto() });
  const abrirAltaCliente = () => { setClientFormData(clienteVacio()); setShowClientModal(true); };

  const [clientFormData, setClientFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    street2: '',
    city: '',
    zip: '',
    taxId: '',
    marginPercentage: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    clientId: '',
    measurementType: 'INT',
    width: '',
    height: '',
    units: 1,
    discount: 0,
    clientReference: '',
    supplierReference: '',
    notes: '',
    description: ''
  });
  
  // Materials state
  const [molduras, setMolduras] = useState([]);
  const [passepartouts, setPassepartouts] = useState([]);
  const [cristales, setCristales] = useState([]);
  const [traseras, setTraseras] = useState([]);
  const [accesorios, setAccesorios] = useState([]);

  // Numeric keypad state (for admin touchscreen use)
  const [numericKeypad, setNumericKeypad] = useState({
    isOpen: false,
    field: null,
    title: '',
    initialValue: '',
    allowDecimals: true
  });

  // Refs for keyboard navigation
  const widthRef = useRef(null);
  const heightRef = useRef(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    if (offlineInitialized) {
      loadInitialData();
      // Focus on width input on mount (only if not in edit mode)
      if (!editMode) {
        widthRef.current?.focus();
      }
    }
  }, [isAdmin, user, offlineInitialized, offlineProducts, offlineClients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form data when in edit mode
  useEffect(() => {
    if (editMode && editData && editOrder && products && Object.keys(products).length > 0 && !editInitialized) {
      console.log('🔧 Initializing edit mode with data:', editData);
      console.log('🔧 Edit order:', editOrder);
      console.log('🔧 Available products:', Object.keys(products));

      // Convert article lines to material arrays
      const newMolduras = [];
      const newPassepartouts = [];
      const newCristales = [];
      const newTraseras = [];
      const newAccesorios = [];

      (editData.lines || []).forEach((line, idx) => {
        const lineType = line.lineType || '';
        const productId = line.productId;

        console.log(`🔧 Processing line ${idx}:`, { lineType, productId, line });

        // Find the product in available products
        let productInfo = null;
        if (lineType === 'moldura' && products.molduras) {
          productInfo = products.molduras.find(p => p.id === productId);
        } else if (lineType.includes('passepartout') && products.passepartout) {
          productInfo = products.passepartout.find(p => p.id === productId);
        } else if (lineType.includes('cristal') && products.cristales) {
          productInfo = products.cristales.find(p => p.id === productId);
        } else if (lineType.includes('trasera') && products.traseras) {
          productInfo = products.traseras.find(p => p.id === productId);
        } else if (lineType.includes('accesorio') && products.accesorios) {
          productInfo = products.accesorios.find(p => p.id === productId);
        }

        console.log(`🔧 Found product info:`, productInfo);

        const materialItem = {
          id: Date.now() + idx,
          productId: productId,
          name: productInfo?.name || line.product?.name || '',
          basePrice: productInfo?.basePrice || line.product?.basePrice || 0,
          unit: productInfo?.unit || line.product?.unit || 'ud'
        };

        if (lineType === 'moldura') {
          newMolduras.push(materialItem);
        } else if (lineType.includes('passepartout')) {
          // Extract passepartout width from metadata or calculate from delta
          // deltaWidth is the difference between line dimensions and base dimensions
          // For passepartout with interior measurement, delta is negative (inner is smaller)
          // passepartout width = |delta| / 2 (applied to both sides)
          let ppWidth = 5; // Default

          if (line.metadata?.passepartoutWidth !== undefined) {
            ppWidth = parseFloat(line.metadata.passepartoutWidth) ?? 5;
          } else if (line._deltaWidth !== undefined) {
            // delta is negative for interior measurements (base - lineWidth)
            // the actual passepartout width is |delta| / 2
            ppWidth = Math.abs(parseFloat(line._deltaWidth)) / 2;
          }

          console.log(`🔧 Passepartout width calculated:`, ppWidth, {
            metadataWidth: line.metadata?.passepartoutWidth,
            deltaWidth: line._deltaWidth
          });

          newPassepartouts.push({ ...materialItem, width: ppWidth });
        } else if (lineType.includes('cristal')) {
          newCristales.push(materialItem);
        } else if (lineType.includes('trasera')) {
          newTraseras.push(materialItem);
        } else if (lineType.includes('accesorio')) {
          newAccesorios.push(materialItem);
        }
      });

      console.log('🔧 Materials parsed:', {
        molduras: newMolduras,
        passepartouts: newPassepartouts,
        cristales: newCristales,
        traseras: newTraseras,
        accesorios: newAccesorios
      });

      // Set form data from article and order FIRST (so client is set before calculation triggers)
      const measurementType = editOrder.metadata?.measurementType ||
                              (editOrder.fingerprint?.startsWith('INT-') ? 'INT' :
                               editOrder.fingerprint?.startsWith('EXT-') ? 'EXT' : 'INT');

      const clientId = editOrder.client?.id || editOrder.clientId || '';

      // IMPORTANT: Get original dimensions (what user entered)
      // Priority 1: Use originalWidth/originalHeight from metadata (stored when creating)
      // Priority 2: Use dimensions from article (which might need adjustment for interior measurements)

      let originalWidth, originalHeight;

      // Check if article has original dimensions stored
      if (editData.originalWidth && editData.originalHeight) {
        originalWidth = parseFloat(editData.originalWidth);
        originalHeight = parseFloat(editData.originalHeight);
        console.log('🔧 Using stored original dimensions:', { originalWidth, originalHeight });
      } else {
        // Fallback: try to get from first line's metadata
        const firstLineWithMeta = (editData.lines || []).find(l => {
          const meta = typeof l.metadata === 'string' ? JSON.parse(l.metadata) : (l.metadata || {});
          return meta.originalWidth && meta.originalHeight;
        });

        if (firstLineWithMeta) {
          const meta = typeof firstLineWithMeta.metadata === 'string'
            ? JSON.parse(firstLineWithMeta.metadata)
            : firstLineWithMeta.metadata;
          originalWidth = parseFloat(meta.originalWidth);
          originalHeight = parseFloat(meta.originalHeight);
          console.log('🔧 Using original dimensions from line metadata:', { originalWidth, originalHeight });
        } else {
          // Last resort: calculate by subtracting passepartout (for legacy orders)
          originalWidth = parseFloat(editData.width) || 0;
          originalHeight = parseFloat(editData.height) || 0;

          if (measurementType === 'INT' && newPassepartouts.length > 0) {
            const maxPpWidth = Math.max(...newPassepartouts.map(p => parseFloat(p.width) || 0));

            if (maxPpWidth > 0) {
              originalWidth = originalWidth - (maxPpWidth * 2);
              originalHeight = originalHeight - (maxPpWidth * 2);

              console.log('🔧 Calculated original dimensions (legacy):', {
                storedWidth: editData.width,
                storedHeight: editData.height,
                maxPpWidth,
                originalWidth,
                originalHeight
              });
            }
          }
        }
      }

      console.log('🔧 Setting form data with:', {
        clientId,
        measurementType,
        width: originalWidth,
        height: originalHeight,
        quantity: editData.quantity,
        discount: editData.discountPercentage
      });

      // Set client info BEFORE form data if available
      if (editOrder.client) {
        setClients(prev => {
          const exists = prev.some(c => c.id === editOrder.client.id);
          if (!exists) {
            return [...prev, editOrder.client];
          }
          return prev;
        });
      }

      // Set form data with ORIGINAL dimensions (not the stored exterior ones)
      setFormData({
        clientId: clientId,
        measurementType: measurementType,
        width: originalWidth > 0 ? originalWidth.toString() : '',
        height: originalHeight > 0 ? originalHeight.toString() : '',
        units: editData.quantity || 1,
        discount: editData.discountPercentage || editOrder?.discountPercentage || 0,
        clientReference: editOrder.clientReference || '',
        supplierReference: editOrder.supplierReference || '',
        notes: editData.notes || editOrder.notes || '', // Line notes take priority over order notes
        description: (() => {
          const meta = editData.lines?.[0]?.metadata;
          const parsed = typeof meta === 'string' ? JSON.parse(meta || '{}') : (meta || {});
          return parsed.customDescription || '';
        })()
      });

      // Set materials AFTER form data
      setMolduras(newMolduras);
      setPassepartouts(newPassepartouts);
      setCristales(newCristales);
      setTraseras(newTraseras);
      setAccesorios(newAccesorios);

      setEditInitialized(true);
      console.log('✅ Edit mode initialized');
    }
  }, [editMode, editData, editOrder, products, editInitialized]);

  // Update ref when loading state changes
  useEffect(() => {
    isLoadingRef.current = loading;
  }, [loading]);

  // Memoize calculatePrice to avoid recreating it on every render
  const memoizedCalculatePrice = React.useCallback(async () => {
    // Prevent multiple simultaneous calculations
    if (isLoadingRef.current) {
      console.log('🚫 Calculation already in progress, skipping...');
      return;
    }

    console.log('🧮 calculatePrice function called in CalculatorPro!');
    console.log('=== DEBUGGING PRODUCTS ===');
    console.log('molduras array:', molduras);
    console.log('molduras IDs:', molduras.map(m => ({
      fullObject: m,
      id: m.id,
      productId: m.productId,
      finalId: m.id || m.productId
    })));

    try {
      setLoading(true);

      const validAccesorios = accesorios.filter(a => (a.productId || a.id) && String(a.productId || a.id).trim() !== '');
      const onlyAccessories = validAccesorios.length > 0 && molduras.length === 0 && passepartouts.length === 0 && cristales.length === 0 && traseras.length === 0;

      const calculationData = {
        clientId: formData.clientId,
        measurementType: formData.measurementType,
        width: onlyAccessories && !formData.width ? 0 : parseFloat(formData.width),
        height: onlyAccessories && !formData.height ? 0 : parseFloat(formData.height),
        units: parseInt(formData.units),
        discount: parseFloat(formData.discount),
        notes: formData.notes,
        clientReference: formData.clientReference,
        supplierReference: formData.supplierReference,
        molduras: molduras.map(m => m.productId || m.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
        passepartouts: passepartouts.map(p => ({
          productId: p.productId || p.id,
          width: parseFloat(p.width) || 0 // Default width 0cm if not specified
        })).filter(p => p.productId && typeof p.productId === 'string' && p.productId.trim() !== ''),
        cristales: cristales.map(c => c.productId || c.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
        traseras: traseras.map(t => t.productId || t.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
        accesorios: accesorios.map(a => a.productId || a.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
        // In TPV/store mode OR when editing a TPV order, always apply 70% margin regardless of client
        // Detect TPV order by: tpvTransaction exists OR metadata.isTPVSale is true
        overrideMargin: (mode === 'store' || (editMode && editOrder && (editOrder.tpvTransaction || editOrder.metadata?.isTPVSale))) ? 70 : (editMode ? 0 : null)
      };

      const response = await calculatorService.calculate(calculationData);
      console.log('=== CALCULATION DEBUG PRO ===');
      console.log('Full response:', response);
      console.log('response.calculation:', response.calculation);
      console.log('response.data:', response.data);

      // The backend returns { success: true, calculation: {...} }
      // And the calculation object should have the client property
      const finalCalculation = response.calculation || response.data?.calculation || response.data || response;
      console.log('Setting calculation to:', finalCalculation);
      console.log('Client in calculation:', finalCalculation.client);
      console.log('Fingerprint in calculation:', finalCalculation.fingerprint);

      // Process items to create grouped details for display
      if (finalCalculation.items && Array.isArray(finalCalculation.items)) {
        // Items from backend already include client margin (but not rounding/discount).
        // We display per-unit component lines and add a small rounding adjustment if needed.
        console.log('Processing items for display:', {
          itemsCount: finalCalculation.items.length,
          subtotal: finalCalculation.subtotal,
          units: finalCalculation.units
        });

        // Group items by dimensions and aggregate by type
        const dimensionGroups = {};

        // Use original form dimensions (not the calculated 20 minimum)
        const originalDimensions = `${formData.height || 0}x${formData.width || 0}`;

        finalCalculation.items.forEach(item => {
          console.log('Processing item:', item);

          // Use original dimensions from form, not the calculated ones
          const dimensions = item.type === 'accesorio' ? '0x0' : originalDimensions;

          if (!dimensionGroups[dimensions]) {
            dimensionGroups[dimensions] = {
              dimensions,
              molduras: [],
              cristales: [],
              traseras: [],
              passepartouts: [],
              accesorios: [],
              totalPrice: 0
            };
          }

          const itemPriceForDisplay = (item.totalPrice || 0);
          dimensionGroups[dimensions].totalPrice += itemPriceForDisplay;

          // Categorize by type
          const type = item.type || 'accesorio';
          switch(type) {
            case 'moldura':
              dimensionGroups[dimensions].molduras.push({...item, priceForDisplay: itemPriceForDisplay});
              break;
            case 'cristal':
              dimensionGroups[dimensions].cristales.push({...item, priceForDisplay: itemPriceForDisplay});
              break;
            case 'trasera':
              dimensionGroups[dimensions].traseras.push({...item, priceForDisplay: itemPriceForDisplay});
              break;
            case 'passepartout':
              dimensionGroups[dimensions].passepartouts.push({...item, priceForDisplay: itemPriceForDisplay});
              break;
            default:
              dimensionGroups[dimensions].accesorios.push({...item, priceForDisplay: itemPriceForDisplay});
          }
        });

        // Create details array for display - show each item separately
        const details = [];

        Object.entries(dimensionGroups).forEach(([dimensions, group]) => {
          if (dimensions !== '0x0') {
            // Show each component as a separate line item
            group.molduras.forEach(item => {
              details.push({
                name: `${dimensions} - Moldura: ${item.name || 'Moldura'}`,
                subtotal: item.priceForDisplay,
                items: [item]
              });
            });

            group.cristales.forEach(item => {
              details.push({
                name: `${dimensions} - Cristal: ${item.name || 'Cristal'}`,
                subtotal: item.priceForDisplay,
                items: [item]
              });
            });

            group.traseras.forEach(item => {
              details.push({
                name: `${dimensions} - Trasera: ${item.name || 'Trasera'}`,
                subtotal: item.priceForDisplay,
                items: [item]
              });
            });

            group.passepartouts.forEach(item => {
              details.push({
                name: `${dimensions} - Passepartout: ${item.name || 'Passepartout'}`,
                subtotal: item.priceForDisplay,
                items: [item]
              });
            });
          }

          // Add accessories separately
          group.accesorios.forEach(acc => {
            details.push({
              name: `Accesorios: ${acc.name || acc.product?.name || 'Accesorio'}`,
              subtotal: acc.priceForDisplay,
              items: [acc]
            });
          });
        });

        // Rounding adjustment so details sum matches backend per-unit subtotal.
        const units = finalCalculation.units || 1;
        const perUnitSubtotal = (finalCalculation.subtotal || 0) / units;
        const perUnitDetailsTotal = details.reduce((sum, d) => sum + (d.subtotal || 0), 0);
        const roundingDiff = perUnitSubtotal - perUnitDetailsTotal;
        if (Math.abs(roundingDiff) >= 0.01) {
          details.push({
            name: 'Ajuste redondeo',
            subtotal: roundingDiff,
            items: []
          });
        }

        finalCalculation.details = details;

        // Log for verification
        const detailsTotal = finalCalculation.details.reduce((sum, d) => sum + d.subtotal, 0);
        console.log('Details created:', finalCalculation.details);
        console.log('Details total with margin:', detailsTotal);
        console.log('Should match subtotal/units:', finalCalculation.subtotal / (finalCalculation.units || 1));
      }

      setCalculation(finalCalculation);

    } catch (error) {
      console.error('Calculation error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al calcular';
      toast.error(`Error: ${errorMsg}`);

      // Log full error details
      if (error.response?.data) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  }, [formData, molduras, passepartouts, cristales, traseras, accesorios, mode]);

  useEffect(() => {
    // IMPORTANT: In edit mode, do NOT auto-calculate
    // User must click "Calcular" to recalculate with current prices
    // This prevents confusion when original prices differ from recalculated prices
    if (editMode) {
      console.log('🔧 Edit mode: skipping auto-calculate');
      return;
    }

    // Auto-calculate when all required fields are filled (with debounce to prevent spam)
    const validAccesoriosForAuto = accesorios.filter(a => (a.productId || a.id) && String(a.productId || a.id).trim() !== '');
    const onlyAccessories = validAccesoriosForAuto.length > 0 && molduras.length === 0 && passepartouts.length === 0 && cristales.length === 0 && traseras.length === 0;
    const hasDimensions = formData.width && formData.height;
    if (formData.clientId && (hasDimensions || onlyAccessories)) {
      const hasAnyMaterial = molduras.length > 0 || passepartouts.length > 0 ||
                            cristales.length > 0 || traseras.length > 0 ||
                            accesorios.length > 0;
      if (hasAnyMaterial) {
        // Debounce auto-calculation to prevent "429 Too Many Requests" error
        const timeoutId = setTimeout(() => {
          // Call memoized function directly
          memoizedCalculatePrice();
        }, 300); // Reduced debounce time to 300ms for faster updates

        return () => clearTimeout(timeoutId);
      } else {
        // Clear calculation when no materials are selected
        setCalculation(null);
      }
    }
  }, [formData, molduras, passepartouts, cristales, traseras, accesorios, memoizedCalculatePrice, editMode]);

  useEffect(() => {
    // Clear calculation when the last item is removed from TPV in store mode
    if (mode === 'store' && ticketItems.length === 0) {
      setCalculation(null);
    }
  }, [ticketItems, mode]);

  // Check material limits and show modal if needed
  useEffect(() => {
    const warnings = checkMaterialLimits();
    if (warnings && warnings.length > 0 && !warningModal) {
      setWarningModal(warnings);
    }
  }, [formData.width, formData.height, formData.measurementType, passepartouts, cristales]);

  // Debug: Log when calculation changes
  useEffect(() => {
    if (calculation) {
      console.log('📊 Calculation updated:', {
        total: calculation.total,
        subtotal: calculation.subtotal,
        timestamp: new Date().toLocaleTimeString()
      });
    }
  }, [calculation]);

  // Reload clients when mode changes (professional vs store/TPV)
  // Only for admin users - non-admin users have their client set in loadInitialData
  useEffect(() => {
    const loadClientsForMode = async () => {
      // Skip for non-admin users - their client is set in loadInitialData
      if (!isAdmin) {
        return;
      }

      try {
        let clientsRes;

        if (editMode) {
          // Edit mode: load ALL clients so user can change to any client
          const response = await api.get('/clients');
          clientsRes = response.data;
        } else if (mode === 'store') {
          // Venta tienda: particulares + la ficha "VENTA TIENDA". Se pide la lista completa
          // porque VENTA TIENDA tiene margen 70 (el de la tienda, correcto) y por la regla
          // "el tipo lo manda el margen" es profesional: con ?type=particular quedaba fuera,
          // no se preseleccionaba y sin cliente no se podían añadir artículos.
          const response = await api.get('/clients');
          const tienda = clientesModoTienda(response.data.clients);
          clientsRes = { clients: tienda.clients };

          // Auto-select "VENTA TIENDA" client if available
          if (tienda.ventaTienda) {
            setFormData(prev => ({ ...prev, clientId: tienda.ventaTienda.id }));
          }
        } else {
          // Professional mode: Load professional clients only
          clientsRes = await calculatorService.getClients();
        }

        setClients(clientsRes.clients || []);
      } catch (error) {
        console.error('Error loading clients for mode:', error);
        toast.error('Error al cargar clientes');
      }
    };

    loadClientsForMode();
  }, [mode, isAdmin, editMode]);

  const loadInitialData = async () => {
    try {
      console.log('Loading initial data...');

      let productsData = offlineProducts;
      let clientsList = offlineClients;

      // When online, always fetch fresh products from API
      if (isOnline) {
        console.log('Online: fetching fresh products from API');
        const productsRes = await calculatorService.getProducts();
        productsData = productsRes.products || productsRes;
      } else if (Object.keys(offlineProducts).length > 0) {
        // Offline: use cached products
        console.log('Offline: using cached products:', Object.keys(offlineProducts).length);
        productsData = offlineProducts;
      }

      // When online, always fetch fresh clients from API
      // Use cache only when offline
      // Backend filters: admin gets all clients, non-admin gets only their client
      if (isOnline) {
        console.log('Online: fetching fresh clients from API');
        if (isAdmin && editMode) {
          // Edit mode: load ALL clients so user can change to any client
          const response = await api.get('/clients');
          clientsList = response.data.clients || [];
        } else if (isAdmin) {
          // Admin: use public calculator endpoint (professional clients only)
          const clientsRes = await calculatorService.getClients();
          clientsList = clientsRes.clients || [];
        } else {
          // Non-admin: backend returns only their client (filtered by email)
          clientsList = await clientService.getAll();
        }
      } else if (offlineClients.length > 0) {
        console.log('Offline: using cached clients:', offlineClients.length);
        clientsList = offlineClients;
      }

      console.log('Products loaded:', Object.keys(productsData).length);
      console.log('Clients loaded:', clientsList.length);

      setProducts(productsData);
      setClients(clientsList);

      // Auto-select client
      if (!isAdmin && clientsList.length > 0) {
        // Non-admin: backend already filtered to only their client
        setFormData(prev => ({ ...prev, clientId: clientsList[0].id }));
      } else if (!isAdmin && clientsList.length === 0) {
        // No client found for this user
        console.error('No client found for user email:', user?.email);
        toast.error('No se encontró un cliente asociado a tu cuenta. Contacta al administrador.');
      } else if (isAdmin && clientsList.length === 1) {
        // Admin with single client: auto-select
        setFormData(prev => ({ ...prev, clientId: clientsList[0].id }));
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al cargar datos';
      toast.error(`Error: ${errorMsg}`);

      // Log full error details
      if (error.response?.data) {
        console.error('Error response:', error.response.data);
      }
    }
  };

  // Client creation mutation
  const createClientMutation = useMutation({
    mutationFn: apiService.createClient,
    onSuccess: (data) => {
      const newClient = data.client || data;
      // "Creado", o aviso amarillo si el backend reutilizó una ficha existente (mismo teléfono/nombre)
      avisarAltaCliente(toast, newClient);

      // El cliente recién creado entra directamente en la lista del modo actual. Antes se
      // llamaba a loadInitialData(), que recarga SIEMPRE la lista de profesionales
      // (/public/calculator/clients): en Venta tienda el cliente nuevo es particular, no
      // estaba en esa lista y el select se quedaba vacío (y la lista pasaba a ser la de
      // profesionales). Con lo que devuelve el POST basta: trae id, nombre, margen y tipo.
      setClients(prev => (prev.some(c => c.id === newClient.id)
        ? prev
        : [...prev, newClient].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'))));

      // Select the newly created client
      setFormData(prev => ({ ...prev, clientId: newClient.id }));

      // Close modal and reset form
      setShowClientModal(false);
      setClientFormData(clienteVacio());
    },
    onError: (error) => {
      mostrarErrorAltaCliente(toast, error, 'Error al crear el cliente: ');
    }
  });

  const handleCreateClient = (e) => {
    e.preventDefault();
    const payload = {
      ...clientFormData,
      name: clientFormData.name?.trim() || '',
      email: clientFormData.email?.trim() || '',
      phone: clientFormData.phone?.trim() || '',
      street: clientFormData.street?.trim() || '',
      street2: clientFormData.street2?.trim() || '',
      city: clientFormData.city?.trim() || '',
      zip: clientFormData.zip?.trim() || '',
      taxId: clientFormData.taxId?.trim() || ''
    };

    if (!payload.phone) {
      toast.error('El teléfono es obligatorio para crear un cliente');
      return;
    }

    createClientMutation.mutate(payload);
  };

  // Helper to normalize decimal input (accept both comma and dot)
  const normalizeDecimal = (value) => {
    if (typeof value === 'string') {
      // Replace comma with dot for decimal separator
      return value.replace(',', '.');
    }
    return value;
  };

  const handleInputChange = (field, value) => {
    // Normalize decimal separator (comma to dot)
    value = normalizeDecimal(value);

    // Convert negative values to positive for numeric fields (except discount which can be negative)
    if (['width', 'height', 'units'].includes(field)) {
      const numValue = parseFloat(value);
      if (value !== '' && numValue < 0) {
        value = Math.abs(numValue).toString();
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Open numeric keypad for touchscreen input (admin only)
  const openNumericKeypad = (field, title, allowDecimals = true) => {
    if (!isAdmin) return; // Only for admin users

    const fieldTitles = {
      width: 'Introducir Ancho (cm)',
      height: 'Introducir Alto (cm)',
      units: 'Introducir Unidades',
      discount: 'Introducir Descuento (%)'
    };

    setNumericKeypad({
      isOpen: true,
      field,
      title: title || fieldTitles[field] || 'Introducir valor',
      initialValue: formData[field]?.toString() || '',
      allowDecimals
    });
  };

  // Handle numeric keypad confirmation
  const handleNumericKeypadConfirm = (value) => {
    if (numericKeypad.field) {
      handleInputChange(numericKeypad.field, value);
    }
    setNumericKeypad({ ...numericKeypad, isOpen: false });
  };

  // Close numeric keypad
  const closeNumericKeypad = () => {
    setNumericKeypad({ ...numericKeypad, isOpen: false });
  };

  const addMaterialOld = (type, setMaterials, materials, maxCount) => {
    if (materials.length < maxCount) {
      // Para passepartout, inicializar con ancho de 0 cm por defecto
      const defaultWidth = type === 'passepartout' ? '0' : '';
      setMaterials([...materials, {
        id: Date.now(),
        productId: '',
        width: defaultWidth,
        name: '',
        basePrice: 0,
        unit: 'ud'
      }]);
    }
  };
  
  const updateMaterial = (type, index, field, value, materials, setMaterials, productInfo = null) => {
    // Normalize decimal separator for width field
    if (field === 'width') {
      value = normalizeDecimal(value);
    }

    // Convert negative values to positive for passepartout width
    if (field === 'width' && type === 'passepartout') {
      const numValue = parseFloat(value);
      if (value !== '' && numValue < 0) {
        value = Math.abs(numValue).toString();
      }
    }

    const updated = [...materials];
    if (field === 'productId' && productInfo) {
      // If updating productId and we have product info, save all product details
      updated[index] = {
        ...updated[index],
        productId: value,
        name: productInfo.name,
        basePrice: productInfo.basePrice,
        unit: productInfo.unit
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setMaterials(updated);
  };
  
  const removeMaterialOld = (index, materials, setMaterials) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  
  // Helper functions for the form
  const getMaterials = (type) => {
    switch(type) {
      case 'molduras': return molduras;
      case 'passepartout': return passepartouts;
      case 'cristales': return cristales;
      case 'traseras': return traseras;
      case 'accesorios': return accesorios;
      default: return [];
    }
  };
  
  const addMaterial = (type, product) => {
    // Correct limits as specified
    const limits = {
      molduras: 3,
      passepartout: 3,
      cristales: 2,
      traseras: 3,
      accesorios: 4
    };
    
    // Ensure the product has both id and productId for compatibility between modes
    const materialItem = {
      ...product,
      productId: product.id, // Ensure productId is set for store mode compatibility
      id: Date.now(), // Unique id for this material instance
      width: type === 'passepartout' ? 0 : undefined // Default width for passepartout
    };
    
    switch(type) {
      case 'molduras':
        if (molduras.length < limits.molduras) {
          setMolduras([...molduras, materialItem]);
        } else {
          toast.warning('Máximo 3 molduras permitidas');
        }
        break;
      case 'passepartout':
        if (passepartouts.length < limits.passepartout) {
          // For passepartouts, we need to add with width field
          setPassepartouts([...passepartouts, { ...materialItem, width: 0 }]);
        } else {
          toast.warning('Máximo 3 passepartouts permitidos');
        }
        break;
      case 'cristales':
        if (cristales.length < limits.cristales) {
          setCristales([...cristales, materialItem]);
        } else {
          toast.warning('Máximo 2 cristales permitidos');
        }
        break;
      case 'traseras':
        if (traseras.length < limits.traseras) {
          setTraseras([...traseras, materialItem]);
        } else {
          toast.warning('Máximo 3 traseras permitidas');
        }
        break;
      case 'accesorios':
        if (accesorios.length < limits.accesorios) {
          setAccesorios([...accesorios, materialItem]);
        } else {
          toast.warning('Máximo 4 accesorios permitidos');
        }
        break;
    }
  };
  
  const removeMaterial = (type, index) => {
    switch(type) {
      case 'molduras':
        setMolduras(molduras.filter((_, i) => i !== index));
        break;
      case 'passepartout':
        setPassepartouts(passepartouts.filter((_, i) => i !== index));
        break;
      case 'cristales':
        setCristales(cristales.filter((_, i) => i !== index));
        break;
      case 'traseras':
        setTraseras(traseras.filter((_, i) => i !== index));
        break;
      case 'accesorios':
        setAccesorios(accesorios.filter((_, i) => i !== index));
        break;
    }
  };

  // Wrapper function for calculatePrice to use in event handlers
  const calculatePrice = () => {
    memoizedCalculatePrice();
  };

  const handleAddToTicketFromCalculator = () => {
    if (!calculation) return;
    setAddToTicketSignal(prev => prev + 1);
  };
  
  const saveOrder = async () => {
    // In edit mode, we recalculate before saving, so calculation can be null
    if (!editMode && !calculation) return;

    const hasSelectedClient = Boolean(formData.clientId || calculation?.client?.id);
    if (!hasSelectedClient) {
      window.alert('Debes seleccionar un cliente antes de enviar el pedido.');
      return;
    }

    // EDIT MODE: Call onSaveEdit callback instead of creating new order
    if (editMode && onSaveEdit) {
      try {
        setLoading(true);
        console.log('=== EDIT MODE SAVE ===');

        // Always recalculate with current materials before saving
        // This ensures calculation.items matches the current material selection
        const calculationData = {
          clientId: formData.clientId,
          measurementType: formData.measurementType,
          width: parseFloat(formData.width),
          height: parseFloat(formData.height),
          units: parseInt(formData.units),
          discount: parseFloat(formData.discount),
          notes: formData.notes,
          molduras: molduras.map(m => m.productId || m.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
          passepartouts: passepartouts.map(p => ({
            productId: p.productId || p.id,
            width: parseFloat(p.width) || 0
          })).filter(p => p.productId && typeof p.productId === 'string' && p.productId.trim() !== ''),
          cristales: cristales.map(c => c.productId || c.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
          traseras: traseras.map(t => t.productId || t.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
          accesorios: accesorios.map(a => a.productId || a.id).filter(id => id && typeof id === 'string' && id.trim() !== ''),
          overrideMargin: (mode === 'store' || (editOrder && (editOrder.tpvTransaction || editOrder.metadata?.isTPVSale))) ? 70 : (editMode ? 0 : null)
        };

        console.log('Recalculating with current materials:', calculationData);
        const response = await calculatorService.calculate(calculationData);
        const freshCalculation = response.calculation || response.data?.calculation || response.data || response;
        console.log('Fresh calculation result:', freshCalculation);

        // If manual price is enabled, modify the calculation total
        const finalCalculation = isManualPriceEnabled && manualPrice !== null
          ? { ...freshCalculation, total: manualPrice, isManualPrice: true, originalTotal: freshCalculation.total }
          : freshCalculation;

        const editResult = {
          calculation: finalCalculation,
          formData: {
            width: parseFloat(formData.width),
            height: parseFloat(formData.height),
            units: parseInt(formData.units),
            discount: parseFloat(formData.discount) || 0,
            measurementType: formData.measurementType,
            notes: formData.notes,
            description: formData.description || '',
            clientId: formData.clientId,
            clientReference: formData.clientReference,
            supplierReference: formData.supplierReference,
            isManualPrice: isManualPriceEnabled,
            manualPrice: isManualPriceEnabled ? manualPrice : null
          },
          materials: {
            molduras: molduras.map(m => m.productId || m.id).filter(Boolean),
            passepartouts: passepartouts.map(p => ({
              productId: p.productId || p.id,
              width: parseFloat(p.width) || 0
            })).filter(p => p.productId),
            cristales: cristales.map(c => c.productId || c.id).filter(Boolean),
            traseras: traseras.map(t => t.productId || t.id).filter(Boolean),
            accesorios: accesorios.map(a => a.productId || a.id).filter(Boolean)
          },
          items: finalCalculation.items || []
        };

        await onSaveEdit(editResult);
        return;
      } catch (error) {
        console.error('Edit save error:', error);
        toast.error('Error al guardar los cambios');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);

      // Debug: log current state
      console.log('=== SAVE ORDER DEBUG ===');
      console.log('formData.clientId:', formData.clientId);
      console.log('calculation.client:', calculation.client);
      console.log('clients list:', clients);
      
      // The calculation should now have the client from the backend
      // But we'll fallback to finding it from the list if needed
      let clientInfo = calculation.client;
      
      if (!clientInfo || !clientInfo.id) {
        // Fallback: find the selected client from the clients list
        const selectedClient = clients.find(c => c.id === formData.clientId);
        console.log('Fallback: Selected client from list:', selectedClient);
        
        if (!selectedClient) {
          window.alert('Debes seleccionar un cliente antes de enviar el pedido.');
          toast.error('Cliente requerido para guardar el pedido');
          console.error('No client found. formData.clientId:', formData.clientId, 'clients:', clients);
          return;
        }
        
        clientInfo = {
          id: selectedClient.id,
          name: selectedClient.name,
          marginPercentage: selectedClient.marginPercentage || 0
        };
      }
      
      // Use the client information (either from calculation or fallback)
      // The calculation.total from backend is already the base price with margin applied
      // For PRO mode: the margin already gives the PVP (IVA included), don't add IVA
      // For store/TPV mode: add IVA on top
      const baseAmount = calculation.total || calculation.subtotal || 0;
      const isStoreMode = mode === 'store';
      const taxAmount = isStoreMode ? baseAmount * 0.21 : 0;
      const finalAmountWithVAT = baseAmount + taxAmount;
      
      // Generate fingerprint for proper grouping (same as TPV does)
      // IMPORTANT: Use ORIGINAL dimensions from form (what user entered), not calculated exterior dimensions
      const generateFingerprint = () => {
        const width = parseFloat(formData.width) || 0;
        const height = parseFloat(formData.height) || 0;
        const measurementType = formData.measurementType || 'INT';
        const discount = parseFloat(formData.discount) || 0;
        const timestamp = Date.now();
        return `${measurementType}-${width}x${height}-${discount}-${timestamp}`;
      };

      const calculationFingerprint = generateFingerprint();

      // Store original dimensions (what user entered) for display purposes
      const originalDimensions = {
        width: parseFloat(formData.width) || 0,
        height: parseFloat(formData.height) || 0,
        measurementType: formData.measurementType || 'INT'
      };

      // Mark all items WITHOUT manual price flag (different from TPV)
      // CalculatorPro saves prices without VAT and without isPriceManual flag
      // Add fingerprint and calculationIndex metadata for proper grouping
      const itemsWithMetadata = calculation.items?.map((item, index) => ({
        ...item,
        isPriceManual: false,  // Important: false for CalculatorPro, true for TPV
        notes: formData.notes || '',  // Add notes from form to each line
        metadata: {
          ...(item.metadata || {}),
          fingerprint: calculationFingerprint,
          calculationIndex: 0,  // Single calculation from CalculatorPro
          productName: item.productName || item.name || 'Artículo',
          // Store original dimensions for display (what user entered)
          originalWidth: originalDimensions.width,
          originalHeight: originalDimensions.height,
          originalMeasurementType: originalDimensions.measurementType,
          // Custom description overrides auto-generated denomination
          ...(formData.description ? { customDescription: formData.description } : {})
        }
      })) || [];
      
      // IMPORTANT: The backend expects:
      // - subtotal: base price with discounts applied but WITHOUT VAT
      // - total: final price WITH VAT
      // This matches what TPV sends
      const calculationWithClient = {
        ...calculation,
        items: itemsWithMetadata,
        fingerprint: calculationFingerprint,  // Add fingerprint to calculation
        client: clientInfo,
        clientReference: formData.clientReference || calculation.clientReference,
        supplierReference: formData.supplierReference || calculation.supplierReference,
        notes: formData.notes || calculation.notes || '', // Include notes in calculation
        calculatedAt: calculation.calculatedAt || new Date(),
        // Match the exact format TPV uses:
        subtotal: baseAmount,  // Base amount without VAT (this will be saved as totalAmount)
        total: finalAmountWithVAT,  // Total with VAT (this will be saved as finalAmount)
        tax: taxAmount,  // VAT amount
        // Keep original values for reference
        pricePerUnit: calculation.pricePerUnit,
        discount: calculation.discount,
        discountAmount: calculation.discountAmount
      };
      
      const orderData = {
        calculation: calculationWithClient,
        clientReference: formData.clientReference,
        supplierReference: formData.supplierReference,
        notes: formData.notes,  // Added notes field
        // Mark as TPV sale when in store mode (so it appears as TPV in orders list)
        metadata: mode === 'store' ? { isTPVSale: true } : undefined
      };
      
      console.log('=== CALCULATORPRO SAVE ORDER ===');
      console.log('Original calculation.total (base without VAT):', calculation.total);
      console.log('Base amount (sin IVA):', baseAmount);
      console.log('Tax amount (21% IVA):', taxAmount);
      console.log('Final with VAT (con IVA):', finalAmountWithVAT);
      console.log('Sending subtotal as:', calculationWithClient.subtotal);
      console.log('Sending total as:', calculationWithClient.total);
      console.log('Full calculation being sent:', calculationWithClient);

	      // Use offline-capable save
	      const saveResult = await saveOrderOffline(orderData);
	      const createdOrder = saveResult.data?.order;

	      if (saveResult.offline) {
	        toast.info('Pedido guardado localmente (pendiente de sincronizar)');
	        return null;
	      } else {
	        toast.success('Pedido guardado');
	      }
	      
	      // Invalidate orders cache to refresh the list
	      queryClient.invalidateQueries(['orders']);
	      
	      // Store the order for TPV functionality
	      setCurrentOrder(createdOrder);
	      
	      // Reset form for next order but keep the current order for TPV
	      resetForm();

	      // Show a message based on user role
      if (isAdmin) {
        toast.info('Pedido guardado. Ahora puedes procesar el pago con TPV 💳');
	      } else {
	        toast.success('Pedido guardado exitosamente ✅');
	      }

	      return createdOrder;
	      
	    } catch (error) {
	      console.error('Save order error:', error);
	      const errorMsg = error.response?.data?.message || error.message || 'Error al guardar';
	      toast.error(`Error: ${errorMsg}`);
      
      // Log full error details
      if (error.response?.data?.details) {
        console.error('Error details:', error.response.data.details);
      }
	    } finally {
	      setLoading(false);
	    }
	  };

	  const openPdfPreview = (pdfBlob, filename) => {
	    const url = window.URL.createObjectURL(pdfBlob);
	    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
	    if (!newWindow) {
	      const link = document.createElement('a');
	      link.href = url;
	      link.download = filename;
	      document.body.appendChild(link);
	      link.click();
	      document.body.removeChild(link);
	    }
	    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
	  };

	  const fetchAndPreviewOrderPdf = async (orderId) => {
	    try {
	      setLoading(true);
	      const pdf = await orderService.getPDF(orderId);
	      openPdfPreview(pdf, `pedido-${orderId}.pdf`);
	    } catch (error) {
	      console.error('Error downloading PDF:', error);
	      toast.error('Error al generar la previsualización del PDF');
	    } finally {
	      setLoading(false);
	    }
	  };

	  const handlePrintOrder = async () => {
	    if (currentOrder?.id) {
	      await fetchAndPreviewOrderPdf(currentOrder.id);
	      return;
	    }

	    const createdOrder = await saveOrder();
	    if (!createdOrder?.id) {
	      if (!navigator.onLine) {
	        toast.warning('Sin conexión: no se puede previsualizar el PDF hasta sincronizar.');
	      }
	      return;
	    }

	    await fetchAndPreviewOrderPdf(createdOrder.id);
	  };
	  
	  const resetForm = () => {
	    setFormData(prev => ({
	      ...prev,
      width: '',
      height: '',
      units: 1,
      discount: 0,
      clientReference: '',
      supplierReference: '',
      notes: '',
      // Resetear también el tipo de medida (checkbox "Medidas exteriores"): resetForm hacía
      // ...prev y no lo incluía, así que el checkbox se quedaba marcado al cerrar/limpiar el pedido.
      measurementType: 'INT'
    }));
    setMolduras([]);
    setPassepartouts([]);
    setCristales([]);
    setTraseras([]);
    setAccesorios([]);
    setCalculation(null);
    widthRef.current?.focus();
  };
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch(e.key) {
          case 'Enter':
            e.preventDefault();
            if (calculation) saveOrder();
            break;
          case 'r':
            e.preventDefault();
            resetForm();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [calculation, saveOrder, resetForm]);
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // State for warning modal
  const [warningModal, setWarningModal] = React.useState(null);

  // Función para verificar límites de materiales
  const checkMaterialLimits = () => {
    const width = parseFloat(formData.width) || 0;
    const height = parseFloat(formData.height) || 0;

    if (!width || !height) return null;

    const warnings = [];

    // Verificar passepartout (120x80 cm)
    if (formData.measurementType === 'INT' && passepartouts.length > 0) {
      const maxPassepartoutWidth = Math.max(...passepartouts.map(p => parseFloat(p.width) || 0));

      if (maxPassepartoutWidth > 0) {
        const exteriorWidth = width + (maxPassepartoutWidth * 2);
        const exteriorHeight = height + (maxPassepartoutWidth * 2);

        if (exteriorWidth > 120 || exteriorHeight > 80) {
          warnings.push({
            type: 'passepartout',
            title: 'Gran Formato Detectado',
            message: `Las dimensiones del passepartout (${exteriorWidth.toFixed(1)} × ${exteriorHeight.toFixed(1)} cm) superan el tamaño estándar.`,
            suggestion: `Para trabajos en gran formato, por favor póngase en contacto con nosotros para ofrecerle la mejor solución.`
          });
        }
      }
    }

    // Verificar cristal (150x100 cm)
    if (cristales.length > 0) {
      let cristalWidth = width;
      let cristalHeight = height;

      // Si es medida interior con passepartouts, calcular dimensión expandida
      if (formData.measurementType === 'INT' && passepartouts.length > 0) {
        const maxPassepartoutWidth = Math.max(...passepartouts.map(p => parseFloat(p.width) || 0));
        cristalWidth = width + (maxPassepartoutWidth * 2);
        cristalHeight = height + (maxPassepartoutWidth * 2);
      }

      if (cristalWidth > 150 || cristalHeight > 100) {
        warnings.push({
          type: 'cristal',
          title: 'Gran Formato Detectado',
          message: `Las dimensiones del cristal (${cristalWidth.toFixed(1)} × ${cristalHeight.toFixed(1)} cm) superan el tamaño estándar.`,
          suggestion: 'Para trabajos en gran formato, por favor póngase en contacto con nosotros para ofrecerle la mejor solución.'
        });
      }
    }

    return warnings.length > 0 ? warnings : null;
  };

  // Función legacy para compatibilidad
  const checkPassepartoutLimits = () => {
    const warnings = checkMaterialLimits();
    if (!warnings) return null;
    const pptWarning = warnings.find(w => w.type === 'passepartout');
    if (!pptWarning) return null;

    // Extraer info del warning para formato legacy
    const match = pptWarning.message.match(/\(([0-9.]+) × ([0-9.]+) cm\)/);
    const maxMatch = pptWarning.suggestion.match(/actualmente ([0-9.]+) cm/);

    return {
      exceeded: true,
      exteriorWidth: match ? match[1] : '0',
      exteriorHeight: match ? match[2] : '0',
      maxPassepartoutWidth: maxMatch ? parseFloat(maxMatch[1]) : 0
    };
  };
  
  const isStoreAddMode = mode === 'store' && !editMode;

  const validAccesoriosForForm = accesorios.filter(a => (a.productId || a.id) && String(a.productId || a.id).trim() !== '');
  const onlyAccessoriesForForm = validAccesoriosForForm.length > 0 && molduras.length === 0 && passepartouts.length === 0 && cristales.length === 0 && traseras.length === 0;
  const dimensionsMissing = !formData.width || !formData.height;
  const dimensionsBlock = dimensionsMissing && !onlyAccessoriesForForm;

  return (
    <Container>
      {/* Edit mode header */}
      {editMode && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: '#e3f2fd',
          borderRadius: '4px',
          border: '1px solid #2196f3'
        }}>
          <h2 style={{ margin: 0, color: '#1976d2', fontSize: '1.1rem' }}>
            ✏️ Editar Artículo - {(() => {
              // Show original dimensions (what user entered), not calculated exterior
              if (editData?.originalWidth && editData?.originalHeight) {
                const type = editData.originalMeasurementType || 'INT';
                return `${type} ${editData.originalWidth}x${editData.originalHeight}`;
              }
              // Fallback: try to get from first line's metadata
              const firstLine = editData?.lines?.[0];
              if (firstLine) {
                const meta = typeof firstLine.metadata === 'string'
                  ? JSON.parse(firstLine.metadata || '{}')
                  : (firstLine.metadata || {});
                if (meta.originalWidth && meta.originalHeight) {
                  const type = meta.originalMeasurementType || 'INT';
                  return `${type} ${meta.originalWidth}x${meta.originalHeight}`;
                }
              }
              // Last fallback: use dimensions as-is
              return editData?.dimensions || 'Sin dimensiones';
            })()}
          </h2>
          {onCancelEdit && (
            <button
              onClick={onCancelEdit}
              style={{
                padding: '0.5rem 1rem',
                background: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              ✕ Cerrar
            </button>
          )}
        </div>
      )}

      {/* Mode toggle - hide in edit mode */}
      {!editMode && isAdmin && (
        <ModeToggle>
          <ModeButton
            $active={mode === 'professional'}
            $isProfessional={true}
            onClick={() => setMode('professional')}
          >
            Calculadora Profesional
          </ModeButton>
          <ModeButton
            $active={mode === 'store'}
            $isProfessional={false}
            onClick={() => setMode('store')}
          >
            Venta Tienda
          </ModeButton>
        </ModeToggle>
      )}

      {mode === 'store' ? (
        <StoreLayout>
          <div className="left-column">
            <div className="calculator-section">
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#333', fontWeight: '600' }}>Calculadora</h3>
              <InputSection $storeMode={true} style={{ padding: '0.5rem', border: 'none', background: 'transparent' }}>
                <CompactForm>
            {/* Basic Info */}
            <InlineGroup columns={isAdmin ? "1fr 40px 55px 55px" : "40px 55px 55px"}>
              {isAdmin && (
              <QuickInput $compact>
                <label>Cliente</label>
                <Select
                  value={(() => {
                    const selectedClient = clients.find(c => c.id === formData.clientId);
                    if (selectedClient) {
                      return {
                        value: selectedClient.id,
                        label: `${selectedClient.name}${clientDiscountSuffix(selectedClient, isAdmin)} (${selectedClient.marginPercentage}%)${(selectedClient.odooArchived ? ' ⚠ archivado en Odoo' : '')}`
                      };
                    }
                    return null;
                  })()}
                  onChange={(selectedOption) => {
                    if (selectedOption?.value === '__CREATE_NEW__') {
                      abrirAltaCliente();
                    } else {
                      handleInputChange('clientId', selectedOption?.value || '');
                    }
                  }}
                  options={[
                    ...clients.map(client => ({
                      value: client.id,
                      label: `${client.name}${clientDiscountSuffix(client, isAdmin)} (${client.marginPercentage}%)${(client.odooArchived ? ' ⚠ archivado en Odoo' : '')}`
                    })),
                    ...(isAdmin ? [{
                      value: '__CREATE_NEW__',
                      label: '➕ Crear nuevo cliente...',
                      isCreateOption: true
                    }] : [])
                  ]}
                  placeholder={clients.length === 0 ? "Cargando clientes..." : "Buscar cliente..."}
                  isClearable={isAdmin && !editMode}
                  isSearchable
                  isDisabled={!isAdmin}
                    autoFocus={clients.length > 1 && !editMode}
                    menuPortalTarget={getSelectPortal()}
                    menuPosition="fixed"
                    filterOption={(option, inputValue) => {
                      // Always show the "Create New" option
                      if (option.value === '__CREATE_NEW__') return true;
                      // Filter other options normally
                      return option.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '28px',
                        height: '28px',
                        fontSize: '0.8rem',
                        padding: 0
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '0 6px',
                        height: '26px'
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: '26px'
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: '3px'
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        padding: '3px'
                      }),
                      menu: (base) => ({
                        ...base,
                        fontSize: '0.8rem',
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      option: (base) => ({
                        ...base,
                        padding: '4px 8px',
                        fontSize: '0.8rem'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: '0.8rem'
                      })
                    }}
                    noOptionsMessage={() => 'No hay clientes'}
                  />
                </QuickInput>
              )}

              <QuickInput $compact>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.measurementType === 'EXT'}
                    onChange={(e) => handleInputChange('measurementType', e.target.checked ? 'EXT' : 'INT')}
                  />
                  {' '}EXT
                </label>
              </QuickInput>
              
              <QuickInput $compact>
                <label>Ancho</label>
                <input
                  ref={widthRef}
                  type="text"
                  inputMode="decimal"
                  value={formData.width}
                  onChange={(e) => handleInputChange('width', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && heightRef.current?.focus()}
                  onClick={() => openNumericKeypad('width', 'Introducir Ancho (cm)', true)}
                  readOnly={isAdmin}
                  style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                />
              </QuickInput>

              <QuickInput $compact>
                <label>Alto</label>
                <input
                  ref={heightRef}
                  type="text"
                  inputMode="decimal"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  onClick={() => openNumericKeypad('height', 'Introducir Alto (cm)', true)}
                  readOnly={isAdmin}
                  style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                />
              </QuickInput>
            </InlineGroup>

            {/* References */}
            <InlineGroup columns="1fr 1fr 60px 60px">
              <QuickInput $compact>
                <label>P.Cliente</label>
                <input
                  type="text"
                  value={formData.clientReference}
                  onChange={(e) => handleInputChange('clientReference', e.target.value)}
                  placeholder="PC-001"
                />
              </QuickInput>

              <QuickInput $compact>
                <label>P.Prov</label>
                <input
                  type="text"
                  value={formData.supplierReference}
                  onChange={(e) => handleInputChange('supplierReference', e.target.value)}
                  placeholder="PP-001"
                />
              </QuickInput>

              <QuickInput $compact>
                <label>Uds</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.units}
                  onChange={(e) => handleInputChange('units', e.target.value)}
                  onClick={() => openNumericKeypad('units', 'Introducir Unidades', false)}
                  readOnly={isAdmin}
                  style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                />
              </QuickInput>

              <QuickInput $compact>
                <label>Dto%</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.discount}
                  onChange={(e) => handleInputChange('discount', e.target.value)}
                  onClick={() => openNumericKeypad('discount', 'Introducir Descuento (%)', true)}
                  readOnly={isAdmin}
                  style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                />
              </QuickInput>
            </InlineGroup>
            
            <QuickInput $compact>
              <label>Descripción</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Dejar vacío para usar la denominación automática"
              />
            </QuickInput>

            <QuickInput $compact>
              <label>Notas</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Añadir notas o instrucciones especiales para este pedido..."
                style={{
                  minHeight: '50px',
                  resize: 'vertical',
                  width: '100%'
                }}
              />
            </QuickInput>
            
            {/* Materials */}
            <div>
              <SectionTitle $compact>Passepartouts</SectionTitle>
              <AddMaterialButton $compact
                onClick={() => addMaterialOld('passepartout', setPassepartouts, passepartouts, 3)}
                disabled={passepartouts.length >= 3}
              >
                + Añadir ({passepartouts.length}/3)
              </AddMaterialButton>
              {passepartouts.map((item, index) => {
                const passepartoutOptions = (products.passepartout || []).map(p => ({
                  value: p.id,
                  label: p.name,
                  product: p
                }));
                const selectedOption = passepartoutOptions.find(opt => opt.value === item.productId) || null;

                return (
                <MaterialLine key={item.id}>
                  <span className="index">{index + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={selectedOption}
                      onChange={(selectedOption) => {
                        updateMaterial('passepartout', index, 'productId', selectedOption?.value || '', passepartouts, setPassepartouts, selectedOption?.product);
                      }}
                      options={passepartoutOptions}
                      placeholder="Buscar passepartout..."
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '32px',
                          fontSize: '0.875rem'
                        }),
                        menu: (base) => ({
                          ...base,
                          fontSize: '0.875rem'
                        })
                      }}
                      noOptionsMessage={() => 'No hay passepartouts'}
                    />
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ancho (cm)"
                    value={item.width || ''}
                    onChange={(e) => updateMaterial('passepartout', index, 'width', e.target.value, passepartouts, setPassepartouts)}
                    style={{
                      width: '80px',
                      padding: '0.25rem',
                      border: '1px solid #ddd',
                      borderRadius: '3px'
                    }}
                  />
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>cm ancho</span>
                  <button onClick={() => removeMaterialOld(index, passepartouts, setPassepartouts)}>×</button>
                </MaterialLine>
                );
              })}
              {(() => {
                const warning = checkPassepartoutLimits();
                if (warning) {
                  return (
                    <WarningMessage>
                      <span className="warning-icon">⚠️</span>
                      <div className="warning-text">
                        <strong>Atención:</strong> Las dimensiones exteriores del passepartout ({warning.exteriorWidth} x {warning.exteriorHeight} cm)
                        superan el tamaño máximo de plancha disponible (120 x 80 cm).
                        Considere reducir el ancho del passepartout ({warning.maxPassepartoutWidth} cm) o consulte opciones especiales para uniones.
                      </div>
                    </WarningMessage>
                  );
                }
                return null;
              })()}
            </div>

            <div>
              <SectionTitle $compact>Cristales</SectionTitle>
              <AddMaterialButton $compact
                onClick={() => addMaterialOld('cristal', setCristales, cristales, 2)}
                disabled={cristales.length >= 2}
              >
                + Añadir ({cristales.length}/2)
              </AddMaterialButton>
              {cristales.map((item, index) => {
                const cristalesOptions = (products.cristales || []).map(p => ({
                  value: p.id,
                  label: p.name,
                  product: p
                }));
                const selectedOption = cristalesOptions.find(opt => opt.value === item.productId) || null;

                return (
                <MaterialLine key={item.id}>
                  <span className="index">{index + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={selectedOption}
                      onChange={(selectedOption) => {
                        updateMaterial('cristal', index, 'productId', selectedOption?.value || '', cristales, setCristales, selectedOption?.product);
                      }}
                      options={cristalesOptions}
                      placeholder="Buscar cristal..."
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '32px',
                          fontSize: '0.875rem'
                        }),
                        menu: (base) => ({
                          ...base,
                          fontSize: '0.875rem'
                        })
                      }}
                      noOptionsMessage={() => 'No hay cristales'}
                    />
                  </div>
                  <span></span>
                  <span></span>
                  <button onClick={() => removeMaterialOld(index, cristales, setCristales)}>×</button>
                </MaterialLine>
                );
              })}
            </div>

            <div>
              <SectionTitle $compact>Traseras</SectionTitle>
              <AddMaterialButton $compact
                onClick={() => addMaterialOld('trasera', setTraseras, traseras, 3)}
                disabled={traseras.length >= 3}
              >
                + Añadir ({traseras.length}/3)
              </AddMaterialButton>
              {traseras.map((item, index) => {
                const traserasOptions = (products.traseras || []).map(p => ({
                  value: p.id,
                  label: p.name,
                  product: p
                }));
                const selectedOption = traserasOptions.find(opt => opt.value === item.productId) || null;

                return (
                <MaterialLine key={item.id}>
                  <span className="index">{index + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={selectedOption}
                      onChange={(selectedOption) => {
                        updateMaterial('trasera', index, 'productId', selectedOption?.value || '', traseras, setTraseras, selectedOption?.product);
                      }}
                      options={traserasOptions}
                      placeholder="Buscar trasera..."
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '32px',
                          fontSize: '0.875rem'
                        }),
                        menu: (base) => ({
                          ...base,
                          fontSize: '0.875rem'
                        })
                      }}
                      noOptionsMessage={() => 'No hay traseras'}
                    />
                  </div>
                  <span></span>
                  <span></span>
                  <button onClick={() => removeMaterialOld(index, traseras, setTraseras)}>×</button>
                </MaterialLine>
                );
              })}
            </div>

            <div>
              <SectionTitle $compact>Molduras</SectionTitle>
              <AddMaterialButton $compact
                onClick={() => addMaterialOld('moldura', setMolduras, molduras, 3)}
                disabled={molduras.length >= 3}
              >
                + Añadir ({molduras.length}/3)
              </AddMaterialButton>
              {molduras.map((item, index) => {
                const moldurasOptions = (products.molduras || []).map(p => ({
                  value: p.id,
                  label: p.name,
                  product: p
                }));
                const selectedOption = moldurasOptions.find(opt => opt.value === item.productId) || null;

                return (
                  <MaterialLine key={item.id}>
                    <span className="index">{index + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Select
                        value={selectedOption}
                        onChange={(selectedOption) => {
                          updateMaterial('moldura', index, 'productId', selectedOption?.value || '', molduras, setMolduras, selectedOption?.product);
                        }}
                        options={moldurasOptions}
                        placeholder="Buscar moldura..."
                        isClearable
                        isSearchable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '32px',
                            fontSize: '0.875rem'
                          }),
                          menu: (base) => ({
                            ...base,
                            fontSize: '0.875rem'
                          })
                        }}
                        noOptionsMessage={() => 'No hay molduras'}
                      />
                    </div>
                    <span></span>
                    <span></span>
                    <button onClick={() => removeMaterialOld(index, molduras, setMolduras)}>×</button>
                  </MaterialLine>
                );
              })}
            </div>

            <div>
              <SectionTitle $compact>Accesorios</SectionTitle>
              <AddMaterialButton $compact
                onClick={() => addMaterialOld('accesorio', setAccesorios, accesorios, 4)}
                disabled={accesorios.length >= 4}
              >
                + Añadir ({accesorios.length}/4)
              </AddMaterialButton>
              {accesorios.map((item, index) => {
                const accesoriosOptions = (products.accesorios || []).map(p => ({
                  value: p.id,
                  label: p.name,
                  product: p
                }));
                const selectedOption = accesoriosOptions.find(opt => opt.value === item.productId) || null;

                return (
                <MaterialLine key={item.id}>
                  <span className="index">{index + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={selectedOption}
                      onChange={(selectedOption) => {
                        updateMaterial('accesorio', index, 'productId', selectedOption?.value || '', accesorios, setAccesorios, selectedOption?.product);
                      }}
                      options={accesoriosOptions}
                      placeholder="Buscar accesorio..."
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '32px',
                          fontSize: '0.875rem'
                        }),
                        menu: (base) => ({
                          ...base,
                          fontSize: '0.875rem'
                        })
                      }}
                      noOptionsMessage={() => 'No hay accesorios'}
                    />
                  </div>
                  <span></span>
                  <span></span>
                  <button onClick={() => removeMaterialOld(index, accesorios, setAccesorios)}>×</button>
                </MaterialLine>
                );
              })}
            </div>
                </CompactForm>
              </InputSection>
            </div>
            
            <div className="price-section">
          <SectionTitle>Cálculo</SectionTitle>
          
          {calculation ? (
            <>
              {ticketItems.length > 0 ? (
                // When there are items in the ticket, show aggregated totals
                <>
                  <PriceLine>
                    <span>Base Total:</span>
                    <span>{formatPrice(ticketItems.reduce((sum, item) => {
                      const base = item.calculation?.costPrice || 0;
                      const qty = item.quantity || 1;
                      return sum + (base * qty);
                    }, 0))}</span>
                  </PriceLine>
                  
                  <PriceLine>
                    <span>Margen Total:</span>
                    <span>{formatPrice(ticketItems.reduce((sum, item) => {
                      const base = item.calculation?.costPrice || 0;
                      const total = item.total || 0;
                      return sum + (total - base);
                    }, 0))}</span>
                  </PriceLine>
                  
                  <PriceLine>
                    <span>Subtotal:</span>
                    <span>{formatPrice(ticketItems.reduce((sum, item) => sum + (item.total || 0), 0))}</span>
                  </PriceLine>
                  
                  <PriceLine $total $large>
                    <span>TOTAL ({ticketItems.length} art.):</span>
                    <span>{formatPrice(ticketItems.reduce((sum, item) => sum + (item.total || 0), 0))}</span>
                  </PriceLine>
                  
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      Artículos en el ticket:
                    </div>
                    {ticketItems.map((item, index) => (
                      <div key={item.id || index} style={{ marginTop: '0.25rem' }}>
                        • {item.name || item.displayName || 'Artículo'}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                // When no items in ticket, show current calculation
                <>
                  <PriceLine>
                    <span>Base:</span>
                    <span>{formatPrice(calculation.costPrice || 0)}</span>
                  </PriceLine>
                  
                  <PriceLine>
                    <span>Margen ({clients.find(c => c.id === formData.clientId)?.marginPercentage}%):</span>
                    <span>{formatPrice((calculation.pricePerUnit || 0) - (calculation.costPrice || 0))}</span>
                  </PriceLine>
                  
                  {formData.units > 1 && (
                    <PriceLine>
                      <span>Precio/ud:</span>
                      <span>{formatPrice(calculation.pricePerUnit)}</span>
                    </PriceLine>
                  )}
                  
                  {formData.discount > 0 && (
                    <PriceLine>
                      <span>Dto. ({formData.discount}%):</span>
                      <span>-{formatPrice(calculation.discountAmount)}</span>
                    </PriceLine>
                  )}
                  
                  <PriceLine>
                    <span>Subtotal:</span>
                    <span>{formatPrice(calculation.subtotal)}</span>
                  </PriceLine>
                  
                  <PriceLine $total $large>
                    <span>TOTAL:</span>
                    <span>{formatPrice(calculation.total)}</span>
                  </PriceLine>
                  
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    <div>Huella: {calculation.fingerprint}</div>
                    <div>{calculation.displayName}</div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '2rem 0' }}>
              Añade materiales para ver el cálculo
            </div>
          )}
          
          <ActionButtons>
            <button
              className="calculate"
              onClick={calculatePrice}
              disabled={loading || !formData.clientId || dimensionsBlock}
            >
              Calcular (F9)
            </button>
            {editMode && onCancelEdit && (
              <button
                className="reset"
                onClick={onCancelEdit}
                disabled={loading}
              >
                Cancelar
              </button>
            )}
            <button
              className="save"
              onClick={isStoreAddMode ? handleAddToTicketFromCalculator : saveOrder}
              disabled={loading || !calculation}
              title={
                isStoreAddMode
                  ? 'Guardar en ticket'
                  : (editMode ? "Guardar cambios" : "Guardar pedido")
              }
            >
              {isStoreAddMode
                ? 'Añadir'
                : (editMode ? '💾 Guardar Cambios' : 'Guardar (Ctrl+Enter)')}
            </button>
          </ActionButtons>
            </div>
          </div>
          
          <div className="tpv-section">
            <TpvDrawer
              isOpen={true}
              onClose={() => setMode('professional')}
              calculation={calculation}
              formData={formData}
              externalAddToTicketSignal={addToTicketSignal}
              onTicketItemsChange={setTicketItems}
              onPaymentSuccess={() => {
                setCalculation(null);
                resetForm();
                toast.success('Pago procesado y pedido creado correctamente');
              }}
              staticMode={true}
            />
          </div>
        </StoreLayout>
      ) : (
        <>
          {/* Layout de dos columnas para pantallas grandes */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>

            {/* COLUMNA IZQUIERDA - PASO 1 */}
            <div style={{ flex: '1 1 600px', minWidth: '300px' }}>
              <div style={{ background: 'white', border: '1px solid #ddd' }}>
                <div style={{ background: '#f5f5f5', padding: '0.75rem 1rem', borderBottom: '1px solid #ddd', fontWeight: '600', fontSize: '0.95rem', textTransform: 'uppercase' }}>
                  PASO - 1 <span style={{ fontWeight: '400' }}>SELECCIONA MATERIALES Y MEDIDAS</span>
                </div>
                <div style={{ padding: '1rem' }}>
                  {/* MEDIDAS EXTERIORES checkbox */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.measurementType === 'EXT'}
                        onChange={(e) => handleInputChange('measurementType', e.target.checked ? 'EXT' : 'INT')}
                        style={{ width: '18px', height: '18px' }}
                      />
                      MEDIDAS EXTERIORES
                    </label>
                  </div>

                  {/* Medidas (cm) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <span style={{ fontWeight: '500' }}>Medidas (cm):</span>
                    <input
                      ref={widthRef}
                      type="text"
                      inputMode="decimal"
                      placeholder="Ancho"
                      value={formData.width}
                      onChange={(e) => handleInputChange('width', e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && heightRef.current?.focus()}
                      onClick={() => openNumericKeypad('width', 'Introducir Ancho (cm)', true)}
                      readOnly={isAdmin}
                      style={{ width: '120px', padding: '0.5rem', border: '1px solid #ccc', fontSize: '1rem', ...(isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}) }}
                    />
                    <input
                      ref={heightRef}
                      type="text"
                      inputMode="decimal"
                      placeholder="Alto"
                      value={formData.height}
                      onChange={(e) => handleInputChange('height', e.target.value)}
                      onClick={() => openNumericKeypad('height', 'Introducir Alto (cm)', true)}
                      readOnly={isAdmin}
                      style={{ width: '120px', padding: '0.5rem', border: '1px solid #ccc', fontSize: '1rem', ...(isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}) }}
                    />
                  </div>

              {/* Material rows with AÑADIR buttons - like the reference design */}
              {[
                { type: 'passepartout', label: 'Passepartout', hint: '* puedes añadir hasta 3 tipos de passepartout.', max: 3 },
                { type: 'cristales', label: 'Cristales', hint: '* puedes añadir hasta 2 tipos de cristales.', max: 2 },
                { type: 'traseras', label: 'Traseras', hint: '* puedes añadir hasta 3 tipos traseras.', max: 3 },
                { type: 'molduras', label: 'Moldura', hint: '* puedes añadir hasta 3 tipos molduras.', max: 3 },
                { type: 'accesorios', label: 'Accesorios', hint: '* puedes añadir hasta 4 tipos de Accesorios.', max: 4 }
              ].map(({ type, label, hint, max }) => {
                const options = (products[type] || []).map(product => ({
                  value: product.id,
                  label: product.name,
                  product: product
                }));
                const materials = getMaterials(type);

                return (
                  <div key={type} style={{ marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <div style={{ width: '200px' }}>
                        <Select
                          options={options}
                          onChange={(selectedOption) => {
                            if (selectedOption?.product && materials.length < max) {
                              addMaterial(type, selectedOption.product);
                            }
                          }}
                          value={null}
                          placeholder="AÑADIR"
                          isClearable={false}
                          isSearchable
                          isDisabled={materials.length >= max}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: '36px',
                              fontSize: '0.9rem',
                              background: state.isDisabled ? '#ccc' : '#5cb85c',
                              borderColor: state.isDisabled ? '#ccc' : '#5cb85c',
                              color: 'white',
                              cursor: state.isDisabled ? 'not-allowed' : 'pointer'
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: 'white',
                              fontWeight: '500'
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              color: 'white'
                            }),
                            menu: (base) => ({
                              ...base,
                              fontSize: '0.9rem'
                            })
                          }}
                          noOptionsMessage={() => 'No hay productos'}
                        />
                      </div>
                      <span style={{ fontWeight: '600' }}>{label}</span>
                      <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>{hint}</span>
                    </div>

                    {/* Show selected materials */}
                    {materials.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginLeft: '0.5rem' }}>
                        {materials.map((item, idx) => (
                          <div key={idx} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            background: '#f5f5f5',
                            border: '1px solid #ddd'
                          }}>
                            <span style={{ fontSize: '0.9rem' }}>{item.name || 'Material'}</span>
                            {type === 'passepartout' && (
                              <>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={item.width || 0}
                                  onChange={(e) => {
                                    let value = normalizeDecimal(e.target.value);
                                    const numValue = parseFloat(value);
                                    if (value !== '' && numValue < 0) {
                                      value = Math.abs(numValue).toString();
                                    }
                                    const updatedPassepartouts = [...passepartouts];
                                    updatedPassepartouts[idx] = { ...updatedPassepartouts[idx], width: value };
                                    setPassepartouts(updatedPassepartouts);
                                  }}
                                  style={{ width: '50px', padding: '0.2rem', border: '1px solid #ccc', fontSize: '0.85rem' }}
                                />
                                <span style={{ fontSize: '0.8rem', color: '#666' }}>cm</span>
                              </>
                            )}
                            <button
                              onClick={() => removeMaterial(type, idx)}
                              style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '1rem' }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Aviso de límites del passepartout */}
              {(() => {
                const warning = checkPassepartoutLimits();
                if (warning) {
                  return (
                    <WarningMessage style={{ marginTop: '1rem' }}>
                      <span className="warning-icon">⚠️</span>
                      <div className="warning-text">
                        <strong>Atención:</strong> Las dimensiones exteriores del passepartout ({warning.exteriorWidth} x {warning.exteriorHeight} cm)
                        superan el tamaño máximo de plancha disponible (120 x 80 cm).
                        Considere reducir el ancho del passepartout ({warning.maxPassepartoutWidth} cm) o consulte opciones especiales para uniones.
                      </div>
                    </WarningMessage>
                  );
                }
                return null;
              })()}

              {/* Descripción personalizada */}
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Descripción</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Dejar vacío para usar la denominación automática"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ccc',
                    fontSize: '0.95rem',
                    borderRadius: '4px'
                  }}
                />
              </div>

              {/* Notas */}
              <div style={{ marginTop: '1rem' }}>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Añade aquí tus notas del pedido..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '0.75rem',
                    border: '1px solid #ccc',
                    fontSize: '0.95rem',
                    resize: 'vertical'
                  }}
                />
              </div>

                  {/* Pedido Cliente / Pedido Proveedor - en línea como en la captura */}
                  <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: '500' }}>Pedido Cliente</span>
                      <input
                        type="text"
                        value={formData.clientReference}
                        onChange={(e) => handleInputChange('clientReference', e.target.value)}
                        placeholder="Pedido Cliente"
                        style={{ width: '150px', padding: '0.5rem', border: '1px solid #ccc', fontSize: '0.95rem' }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: '500' }}>Pedido Proveedor</span>
                      <input
                        type="text"
                        value={formData.supplierReference}
                        onChange={(e) => handleInputChange('supplierReference', e.target.value)}
                        placeholder="Pedido Proveedor"
                        style={{ width: '150px', padding: '0.5rem', border: '1px solid #ccc', fontSize: '0.95rem' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* FIN COLUMNA IZQUIERDA */}

            {/* COLUMNA DERECHA - SELECCIONA CLIENTE + PASO 2 */}
            <div style={{ flex: '0 0 350px', minWidth: '300px' }}>
              {/* SELECCIONA CLIENTE - Solo visible para admin */}
              {isAdmin && (
                <div style={{ background: 'white', border: '1px solid #ddd', marginBottom: '1rem' }}>
                  <div style={{ background: '#f5f5f5', padding: '0.75rem 1rem', borderBottom: '1px solid #ddd', fontWeight: '600', fontSize: '0.95rem', textTransform: 'uppercase' }}>
                    SELECCIONA CLIENTE
                  </div>
                  <div style={{ padding: '1rem' }}>
                    <Select
                      value={(() => {
                        const selectedClient = clients.find(c => c.id === formData.clientId);
                        if (selectedClient) {
                          return {
                            value: selectedClient.id,
                            label: `${selectedClient.name}${clientDiscountSuffix(selectedClient, isAdmin)} (${selectedClient.marginPercentage}%)${(selectedClient.odooArchived ? ' ⚠ archivado en Odoo' : '')}`
                          };
                        }
                        return null;
                      })()}
                      onChange={(selectedOption) => {
                        if (selectedOption?.value === '__CREATE_NEW__') {
                          abrirAltaCliente();
                        } else {
                          handleInputChange('clientId', selectedOption?.value || '');
                        }
                      }}
                      options={[
                        ...clients.map(client => ({
                          value: client.id,
                          label: `${client.name}${clientDiscountSuffix(client, isAdmin)} (${client.marginPercentage}%)${(client.odooArchived ? ' ⚠ archivado en Odoo' : '')}`
                        })),
                        {
                          value: '__CREATE_NEW__',
                          label: '➕ Crear nuevo cliente...',
                          isCreateOption: true
                        }
                      ]}
                      placeholder={clients.length === 0 ? "Cargando clientes..." : "Buscar cliente..."}
                      isClearable={!editMode}
                      isSearchable
                      isDisabled={false}
                      menuPortalTarget={getSelectPortal()}
                      menuPosition="fixed"
                      filterOption={(option, inputValue) => {
                        if (option.value === '__CREATE_NEW__') return true;
                        return option.label.toLowerCase().includes(inputValue.toLowerCase());
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '40px',
                          fontSize: '1rem',
                          backgroundColor: base.backgroundColor
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }}
                      noOptionsMessage={() => 'No hay clientes'}
                    />
                  </div>
                </div>
              )}

              {/* PASO 2 - CALCULAR */}
              <div style={{ background: 'white', border: '1px solid #ddd' }}>
                <div style={{ background: '#f5f5f5', padding: '0.75rem 1rem', borderBottom: '1px solid #ddd', fontWeight: '600', fontSize: '0.95rem', textTransform: 'uppercase' }}>
                  PASO - 2 <span style={{ fontWeight: '400' }}>CALCULAR</span>
                </div>
                <div style={{ padding: '1rem' }}>
                  {/* Botón grande CALCULAR */}
                  <button
                    onClick={calculatePrice}
                    disabled={loading || dimensionsBlock}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '1.5rem',
                      fontWeight: '500',
                      background: loading || dimensionsBlock ? '#ccc' : '#5cb85c',
                      color: 'white',
                      border: 'none',
                      cursor: loading || dimensionsBlock ? 'not-allowed' : 'pointer',
                      marginBottom: '1rem'
                    }}
                  >
                    {loading ? 'Calculando...' : 'Calcular'}
                  </button>

                  {/* Huella, Descuento, Unidades */}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: '500' }}>Huella:</span> {calculation?.fingerprint || ''}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: '500' }}>Descuento comercial:</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.discount}
                      onChange={(e) => handleInputChange('discount', e.target.value)}
                      onClick={() => openNumericKeypad('discount', 'Introducir Descuento (%)', true)}
                      readOnly={isAdmin}
                      style={{ width: '50px', padding: '0.3rem', border: '1px solid #ccc', textAlign: 'center', ...(isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}) }}
                    />
                    <span>%</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '500' }}>Unidades:</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.units}
                      onChange={(e) => handleInputChange('units', e.target.value)}
                      onClick={() => openNumericKeypad('units', 'Introducir Unidades', false)}
                      readOnly={isAdmin}
                      style={{ width: '50px', padding: '0.3rem', border: '1px solid #ccc', textAlign: 'center', ...(isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}) }}
                    />
                  </div>

                  {/* TOTAL */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                      TOTAL: {calculation ? `${(isManualPriceEnabled && manualPrice !== null ? manualPrice : calculation.total)?.toFixed(2)}€` : ''}
                    </div>
                    {editMode && calculation && (
                      <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                        Importe {isEditingTPVOrder ? 'con IVA' : 'sin IVA'}
                        {parseFloat(formData.discount || 0) > 0 ? ' (con descuento aplicado)' : ''}.
                      </div>
                    )}

                    {/* Manual price override for edit mode */}
                    {editMode && calculation && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={isManualPriceEnabled}
                            onChange={(e) => {
                              setIsManualPriceEnabled(e.target.checked);
                              if (e.target.checked && manualPrice === null) {
                                setManualPrice(calculation.total);
                              }
                            }}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span style={{ fontWeight: '500', fontSize: '0.9rem' }}>Modificar precio manualmente</span>
                        </label>

                        {isManualPriceEnabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={manualPrice ?? ''}
                              onChange={(e) => {
                                // No forzar 0 al vaciar: si no, queda siempre un "0" delante
                                // (p.ej. al teclear "71" sobre el 0 forzado salía "071").
                                const v = e.target.value;
                                if (v === '') { setManualPrice(null); return; }
                                const num = parseFloat(v);
                                if (!isNaN(num)) setManualPrice(num);
                              }}
                              style={{
                                flex: 1,
                                padding: '0.5rem',
                                fontSize: '1.2rem',
                                fontWeight: '600',
                                border: '2px solid #ffc107',
                                borderRadius: '4px',
                                textAlign: 'right'
                              }}
                            />
                            <span style={{ fontSize: '1.2rem', fontWeight: '600' }}>€</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

	                  {/* Botones de acción - Enviar/Guardar e Imprimir en línea */}
	                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
	                    {editMode && onCancelEdit && (
	                      <button
	                        onClick={onCancelEdit}
	                        disabled={loading}
	                        style={{
	                          flex: 1,
	                          padding: '0.75rem',
	                          fontSize: '1rem',
	                          fontWeight: '500',
	                          background: loading ? '#ccc' : '#f0ad4e',
	                          color: 'white',
	                          border: 'none',
	                          cursor: loading ? 'not-allowed' : 'pointer'
	                        }}
	                      >
	                        Cancelar
	                      </button>
	                    )}
	                    <button
	                      onClick={isStoreAddMode ? handleAddToTicketFromCalculator : saveOrder}
	                      disabled={!calculation || loading}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        fontSize: '1rem',
                        fontWeight: '500',
                        background: !calculation || loading ? '#ccc' : '#5cb85c',
                        color: 'white',
                        border: 'none',
                        cursor: !calculation || loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isStoreAddMode
                        ? 'Guardar'
                        : (editMode ? 'Guardar Cambios' : 'Enviar Pedido')}
	                    </button>
	                    {!editMode && (
	                      <button
	                        onClick={handlePrintOrder}
	                        disabled={loading || (!calculation && !currentOrder?.id)}
	                        style={{
	                          flex: 1,
	                          padding: '0.75rem',
	                          fontSize: '1rem',
	                          fontWeight: '500',
	                          background: (loading || (!calculation && !currentOrder?.id)) ? '#ccc' : '#5cb85c',
	                          color: 'white',
	                          border: 'none',
	                          cursor: (loading || (!calculation && !currentOrder?.id)) ? 'not-allowed' : 'pointer'
	                        }}
	                      >
	                        Imprimir pedido
	                      </button>
	                    )}
	                  </div>

                  {/* Borrar Todo */}
                  <button
                    onClick={resetForm}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      fontWeight: '500',
                      background: '#f0ad4e',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      marginBottom: '0.75rem'
                    }}
                  >
                    Borrar Todo
                  </button>

                  
                </div>
              </div>
            </div>
            {/* FIN COLUMNA DERECHA */}

          </div>
          {/* FIN LAYOUT DOS COLUMNAS */}
        </>
      )}

      {/* Client Creation Modal */}
      {showClientModal && (
        <Modal onClick={() => setShowClientModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Crear Nuevo Cliente</ModalTitle>
            <Form onSubmit={handleCreateClient}>
              {/* Fila 1: Nombre | Teléfono */}
              <FormGroup>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  type="text"
                  value={clientFormData.name}
                  onChange={(e) => setClientFormData({ ...clientFormData, name: e.target.value })}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Teléfono *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={clientFormData.phone}
                  onChange={(e) => setClientFormData({ ...clientFormData, phone: e.target.value })}
                  required
                />
              </FormGroup>

              {/* Fila 2: Email | NIF/CIF */}
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={clientFormData.email}
                  onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="taxId">NIF/CIF</Label>
                <Input
                  id="taxId"
                  type="text"
                  value={clientFormData.taxId}
                  onChange={(e) => setClientFormData({ ...clientFormData, taxId: e.target.value })}
                />
              </FormGroup>

              {/* Fila 3: Dirección (full width) */}
              <FullWidthGroup>
                <FormGroup>
                  <Label htmlFor="street">Dirección</Label>
                  <Input
                    id="street"
                    type="text"
                    value={clientFormData.street}
                    onChange={(e) => setClientFormData({ ...clientFormData, street: e.target.value })}
                  />
                </FormGroup>
              </FullWidthGroup>

              {/* Fila 4: Ciudad | Código Postal */}
              <FormGroup>
                <Label htmlFor="city">Ciudad</Label>
                <Input
                  id="city"
                  type="text"
                  value={clientFormData.city}
                  onChange={(e) => setClientFormData({ ...clientFormData, city: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="zip">Código Postal</Label>
                <Input
                  id="zip"
                  type="text"
                  value={clientFormData.zip}
                  onChange={(e) => setClientFormData({ ...clientFormData, zip: e.target.value })}
                />
              </FormGroup>
              {/* Sin campo de margen: lo fija el modo (venta tienda = 0 particular, profesional = 100) y se puede cambiar después en Clientes */}

              {/* Botones (full width) */}
              <FullWidthGroup>
                <ButtonGroup>
                <Button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setShowClientModal(false);
                    setClientFormData(clienteVacio());
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="primary"
                  disabled={createClientMutation.isLoading}
                >
                  {createClientMutation.isLoading ? 'Creando...' : 'Crear Cliente'}
                </Button>
              </ButtonGroup>
              </FullWidthGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* Warning Modal for Material Limits */}
      {warningModal && warningModal.length > 0 && (
        <WarningModal onClick={(e) => {
          // Prevent closing by clicking the backdrop
          e.stopPropagation();
        }}>
          <WarningModalContent onClick={(e) => e.stopPropagation()}>
            <WarningModalClose onClick={() => setWarningModal(null)}>
              ✕
            </WarningModalClose>

            {warningModal.map((warning, index) => (
              <div key={index} style={{ marginBottom: index < warningModal.length - 1 ? '1.5rem' : '0' }}>
                <WarningModalHeader>
                  <span className="icon">⚠️</span>
                  <h3>{warning.title}</h3>
                </WarningModalHeader>

                <WarningModalBody>
                  <p><strong>{warning.message}</strong></p>
                  <p>{warning.suggestion}</p>
                </WarningModalBody>
              </div>
            ))}
          </WarningModalContent>
        </WarningModal>
      )}

      {/* Numeric Keypad for Admin Touchscreen Input */}
      {isAdmin && (
        <NumericKeypad
          isOpen={numericKeypad.isOpen}
          onClose={closeNumericKeypad}
          onConfirm={handleNumericKeypadConfirm}
          initialValue={numericKeypad.initialValue}
          title={numericKeypad.title}
          allowDecimals={numericKeypad.allowDecimals}
        />
      )}
    </Container>
  );
};

export default CalculatorPro;
